/**
 * SpeakButton — tiny "🔊 Listen" toggle that streams server TTS for an
 * assistant message. Pause/resume by clicking again. One playback at a time
 * — clicking on a different message stops the previous audio.
 */

import { useEffect, useRef, useState } from 'react';
import { Volume2, Pause, Loader2 } from 'lucide-react';
import { apiFetch } from '../fetchInterceptor';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Singleton audio element so only one message plays at a time across the page.
let currentAudio = null;
let currentMessageId = null;
const listeners = new Set();
const broadcast = () => listeners.forEach((fn) => fn(currentMessageId));

export default function SpeakButton({ text, messageId }) {
  const [busy, setBusy] = useState(false);
  const [playingThis, setPlayingThis] = useState(false);
  const objectUrlRef = useRef(null);

  useEffect(() => {
    const onChange = (id) => setPlayingThis(id === messageId);
    listeners.add(onChange);
    return () => listeners.delete(onChange);
  }, [messageId]);

  const stopCurrent = () => {
    if (currentAudio) {
      try { currentAudio.pause(); } catch { /* */ }
      try { currentAudio.src = ''; } catch { /* */ }
      currentAudio = null;
    }
    currentMessageId = null;
    broadcast();
  };

  const toggle = async () => {
    if (playingThis) {
      stopCurrent();
      return;
    }
    if (!text || busy) return;

    stopCurrent();
    setBusy(true);
    try {
      const r = await apiFetch(`${API_URL}/api/tts/speak`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, voice: 'alloy' }),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.detail || 'TTS failed');
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      objectUrlRef.current = url;
      const audio = new Audio(url);
      audio.onended = () => {
        if (currentMessageId === messageId) {
          currentMessageId = null;
          broadcast();
        }
        try { URL.revokeObjectURL(url); } catch { /* */ }
      };
      currentAudio = audio;
      currentMessageId = messageId;
      broadcast();
      await audio.play();
    } catch (e) {
      toast.error(e?.message || 'Voice playback failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={busy}
      className={cn(
        'inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full text-[10px] font-medium transition-colors',
        playingThis
          ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400'
          : 'text-muted-foreground hover:bg-muted/60',
      )}
      title={playingThis ? 'Stop' : 'Listen'}
      data-testid="ai-hub-speak-btn"
    >
      {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : playingThis ? <Pause className="w-3 h-3" /> : <Volume2 className="w-3 h-3" />}
      {playingThis ? 'Stop' : 'Listen'}
    </button>
  );
}
